import * as THREE from 'three';
import type { PackModel } from './modelLibrary';
import { tuneMeshyMaterial, type MeshyTune } from './tune';

/**
 * Interior furniture from a scene pack (docs/research/MESHY-WORLD-PLAN.md, P2c): one textured mesh per piece,
 * drawn only while its scene (the hideout, the back room) is up. The geometry and texture belong to the pack
 * (released with the scene, modelLibrary.releaseModelPack); the material made here is the caller's to dispose.
 * A model with a glow mask lights its masked texels (atlas alpha 255 = unlit … 128 = lit) by `setGlow`.
 */
export interface InteriorModel {
  mesh: THREE.Mesh;
  material: THREE.MeshStandardMaterial;
  /** Emissive level of the masked texels (0 = off). */
  setGlow(level: number): void;
}

export function interiorModel(model: PackModel, tune: MeshyTune = {}): InteriorModel {
  const material = tuneMeshyMaterial(new THREE.MeshStandardMaterial({ map: model.texture }), { envMapIntensity: 0.5, ...tune });
  const glow = { value: 0 };
  if (model.glow) {
    material.onBeforeCompile = (shader) => {
      shader.uniforms.uMeshyGlow = glow;
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', '#include <common>\nuniform float uMeshyGlow;')
        .replace(
          '#include <emissivemap_fragment>',
          `#include <emissivemap_fragment>
  #ifdef USE_MAP
    vec4 bhGlowTexel = texture2D( map, vMapUv );
    totalEmissiveRadiance += bhGlowTexel.rgb * clamp( ( 1.0 - bhGlowTexel.a ) * 2.0, 0.0, 1.0 ) * uMeshyGlow;
  #endif`,
        );
    };
    material.customProgramCacheKey = () => 'meshyglow';
  }
  const mesh = new THREE.Mesh(model.geometry, material);
  mesh.name = `meshy-${model.id}`;
  return {
    mesh,
    material,
    setGlow(level) {
      glow.value = level;
    },
  };
}
